// src/components/social/MessageItem.jsx
import React, { useContext, useState, useRef, useEffect } from 'react';
import { UserContext } from '../../context/userContext';
import { MessageContext } from '../../context/messageContext';
import ReactTimeAgo from 'react-time-ago';
import { FaEllipsisV, FaTrash, FaCheck, FaCheckDouble } from 'react-icons/fa';

const MessageItem = ({ message }) => {
  const [showOptions, setShowOptions] = useState(false);
  const { currentUser } = useContext(UserContext);
  const { deleteMessage } = useContext(MessageContext);
  const optionsMenuRef = useRef(null);
  
  const isOwnMessage = message.sender._id === currentUser?.id;

  // Handle click outside to close options menu
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (optionsMenuRef.current && !optionsMenuRef.current.contains(event.target)) {
        setShowOptions(false);
      }
    };

    if (showOptions) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showOptions]);

  // Format time to display
  const formatTime = (date) => {
    return new Date(date).toLocaleTimeString([], { 
      hour: '2-digit', 
      minute: '2-digit' 
    });
  };

  // Handle message deletion
  const handleDelete = async () => {
    if (window.confirm('Are you sure you want to delete this message?')) {
      await deleteMessage(message._id);
      setShowOptions(false);
    }
  };

  // Render status icon based on message status
  const renderStatusIcon = () => {
    if (!isOwnMessage) return null;
    
    switch (message.status) {
      case 'sent':
        return <FaCheck className="text-gray-400" size={12} />;
      case 'delivered':
        return <FaCheck className="text-blue-400" size={12} />;
      case 'read':
        return <FaCheckDouble className="text-blue-500" size={12} />;
      default:
        return null;
    }
  };

  return (
    <div className={`flex mb-4 ${isOwnMessage ? 'justify-end' : 'justify-start'}`}>
      <div className={`relative max-w-[75%] ${isOwnMessage ? 'order-1' : 'order-none'}`}>
        <div 
          className={`relative rounded-lg p-3 shadow-sm ${
            isOwnMessage 
              ? 'bg-primary text-white rounded-br-none' 
              : 'bg-white text-gray-800 rounded-bl-none'
          }`}
        >
          {message.content && (
            <div className="whitespace-pre-wrap break-words">{message.content}</div>
          )}
          
          {message.media && (
            <div className="mt-2 rounded-md overflow-hidden">
              <img 
                src={`${process.env.REACT_APP_ASSETS_URL}/uploads/media/${message.media}`} 
                alt="Message attachment" 
                className="max-w-full h-auto"
              />
            </div>
          )}
          
          <div className={`flex items-center gap-1 mt-1 text-xs ${
            isOwnMessage ? 'text-blue-100' : 'text-gray-500'
          }`}>
            <span>{formatTime(message.createdAt)}</span>
            {renderStatusIcon()}
          </div>
        </div>
        
        {/* Message options */}
        <div className="absolute top-2 right-2" ref={optionsMenuRef}>
          <button 
            className={`p-1 rounded-full ${
              isOwnMessage
                ? 'text-blue-100 hover:bg-blue-600'
                : 'text-gray-400 hover:bg-gray-100'
            }`}
            onClick={() => setShowOptions(!showOptions)}
          >
            <FaEllipsisV size={12} />
          </button>
          
          {showOptions && (
            <div className="absolute right-0 top-full mt-1 w-32 bg-white rounded-md shadow-lg py-1 z-10">
              <button 
                onClick={handleDelete} 
                className="w-full text-left px-4 py-2 text-sm text-red-600 hover:bg-gray-100 flex items-center gap-2"
              >
                <FaTrash size={12} /> Delete
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default MessageItem;